import React, { useEffect, useState } from "react";
import { debounce, get } from "lodash";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockHead,
  Icon,
  RSelect,
} from "../../components/Component";
import { Button } from "reactstrap";
import BasePaginationTable from "../../components/table/BasePaginationTable";
import UpdateCategoryModal from "./modals/UpdateCategoryModal";
import DeleteConfirmationModal from "./modals/DeleteConfimationModal";
import { useSelector } from "react-redux";
import { deleteCategory } from "../../api/category";
import { toast } from "react-toastify";
import { deleteCategorySuccess, getListCategory } from "../../slice/categories";
import { useDispatch } from "react-redux";
import { categoryTypes, defaultParams } from "../../constants";
import TableActions from "../../components/table/TableActions";

// Categories store name as { en, ar? }. Legacy rows may still be plain
// strings until the BE migration runs — handle both shapes.
const getCategoryName = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en ?? "";
};
const getCategoryNameAr = (name) => {
  if (!name || typeof name === "string") return "";
  return name.ar ?? "";
};

function getDefaultColumns(onEditClick, onDeleteClick) {
  return [
    {
      head: <span className="sub-text">{"Name"}</span>,
      cell: (row) => {
        const en = getCategoryName(row.name);
        const ar = getCategoryNameAr(row.name);
        return (
          <div className="d-flex flex-column">
            <span>{en || "-"}</span>
            {ar && (
              <span className="text-muted" style={{ fontSize: 12 }}>
                {ar}
              </span>
            )}
          </div>
        );
      },
    },
    {
      head: <span className="sub-text">{"Parent"}</span>,
      cell: (row) => <span>{getCategoryName(row.parent?.name) || "-"}</span>,
    },
    {
      head: <span className="sub-text">{"Type"}</span>,
      cell: (row) => <span>{row.type}</span>,
    },
    {
      head: <span className="sub-text"></span>,
      cellClassName: "nk-tb-col-tools",
      cell: (row) => (
        <TableActions
          actions={[
            { label: "Edit", icon: "eye", onClick: () => onEditClick(row) },
            {
              label: "Delete",
              icon: "delete",
              danger: true,
              onClick: () => onDeleteClick(row),
            },
          ]}
        />
      ),
    },
  ];
}
const CategoriesPage = () => {
  const { list, summary, loading } = useSelector((state) => state.categories);
  const [params, setParams] = useState(defaultParams);
  const dispatch = useDispatch();

  const [smOption, setSmOption] = useState(false);

  const [isCategoryModalVisible, setCategoryModalVisible] = useState({
    category: null,
    update: false,
    delete: false,
  });
  const onDeleteCategory = async () => {
    try {
      await deleteCategory(isCategoryModalVisible.category?._id);
      dispatch(deleteCategorySuccess(isCategoryModalVisible.category));
      toast.success("Delete category successfully");
    } catch (error) {
      toast.error(error);
    }
  };
  const openModal = (category) => {
    setCategoryModalVisible({ category, update: true });
  };
  const closeModal = () => {
    setCategoryModalVisible({
      category: null,
      delete: false,
      update: false,
    });
  };
  const onDelete = (category) => {
    setCategoryModalVisible({ category, delete: true });
  };
  const dataTableColumns = getDefaultColumns(openModal, onDelete);
  useEffect(() => {
    dispatch(getListCategory(params));
  }, [dispatch, params]);
  const handlePageChange = (page) => {
    setParams({
      ...params,
      page,
    });
  };

  const _handleInputChange = (value) => {
    setParams((prev) => ({
      ...prev,
      keyword: value,
    }));
  };
  const handleInputChange = debounce(_handleInputChange, 500);

  return (
    <React.Fragment>
      <Head title="Categories"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Categories</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand me-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div
                        className="form-control-wrap"
                        style={{
                          height: "38px",
                        }}
                      >
                        <div className="form-icon form-icon-right">
                          <Icon name="search"></Icon>
                        </div>
                        <input
                          type="text"
                          className="form-control h-100"
                          id="default-04"
                          placeholder="Search by name"
                          onChange={(e) => handleInputChange(e.target.value)}
                        />
                      </div>
                    </li>
                    <li className="w-250px">
                      <div className="w-70">
                        <RSelect
                          options={categoryTypes}
                          isMulti
                          onChange={(val) => {
                            setParams({
                              ...params,
                              page: 1,
                              types: val.map((e) => e.value),
                            });
                          }}
                          placeholder="Any Type"
                        />
                      </div>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Button
                        className="toggle btn-icon d-md-none"
                        color="primary"
                        onClick={() => {
                          openModal();
                        }}
                      >
                        <Icon name="plus"></Icon>
                      </Button>
                      <Button
                        className="toggle d-none d-md-inline-flex"
                        color="primary"
                        onClick={() => {
                          openModal();
                        }}
                      >
                        <Icon name="plus"></Icon>
                        <span>Add Category</span>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <BasePaginationTable
          list={list}
          loading={loading}
          columns={dataTableColumns}
          onPageChange={handlePageChange}
          hasNextPage={summary?.hasNextPage}
          totalItems={summary?.count}
          {...params}
        />
      </Content>
      <UpdateCategoryModal
        category={isCategoryModalVisible.category}
        isOpen={isCategoryModalVisible.update}
        closeModal={closeModal}
      />
      <DeleteConfirmationModal
        onConfirm={onDeleteCategory}
        isOpen={isCategoryModalVisible.delete}
        onCloseModal={closeModal}
      />
    </React.Fragment>
  );
};

export default CategoriesPage;
